import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';
import { COMMAND_FUNNEL_LABELS, type DropOff, type ReasonCount } from '@nest-recruit/shared';
import { Card } from '../../../components/Card';
import { DataTable } from '../../../components/data-table/DataTable';
import { sortRows, type SortReaders } from '../../../components/data-table/sort-rows';
import type { Column, SortSpec } from '../../../components/data-table/types';
import { SkeletonLines } from '../../../components/Skeleton';
import { ToneIcon, toneCardClass } from '../../../components/Tone';
import { useToneArea } from '../../../components/ToneArea';
import { ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { TableSection } from '../../../components/TableSection';
import { formatNumber } from '../../../lib/format';
import { EMPTY_SELECTION, type SelectionState } from '../../../lib/selection';
import { useCanView } from '../../../stores/session';
import { DeepLink } from '../../notifications/components/DeepLink';
import { analyticsDrillHref, useDropOff, type DropOffUrl } from '../api/analytics-api';
import { AnalyticsFilters } from '../components/AnalyticsFilters';
import { DictionaryLine, DrillCount, ExportLinks, HowCounted } from '../components/AnalyticsBits';
import { moneyList, percent } from '../report-format';
import styles from '../Reports.module.css';

const REPORT = 'drop_off';
const SOURCES = 'source_effectiveness';

/**
 * RPT-05 — drop-off and source effectiveness (master plan S29, §L27 RPT-05a/b). Where candidates
 * leave and why. WD-PROCESS and WD-EXPERIENCE come first and in red: "those are candidates telling
 * you your operation is failing". N-61 lands on that section.
 */
export function DropOffPage(): ReactNode {
  const canView = useCanView('reports_own');
  const search: DropOffUrl = useSearch({ from: '/app-layout/reports/drop-off' });
  const navigate = useNavigate();
  const { section, ...url } = search;
  const report = useDropOff(url, canView);
  const data = report.data;
  const hasData = data !== undefined;

  useEffect(() => {
    if (section && hasData) document.getElementById(`drop-off-${section}`)?.scrollIntoView({ block: 'start' });
  }, [section, hasData]);

  if (!canView || report.error?.status === 403) return <ForbiddenState resource="the drop-off analysis" contact="Your admin" />;
  if (report.error && !data) return <ServerErrorState onRetry={() => void report.refetch()} />;

  const setFilters = (next: DropOffUrl): void => void navigate({ to: '/reports/drop-off', search: next, replace: true });

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>Drop-off analysis</h1>
          <p className={styles.lead}>Where candidates leave, and why — rejections, withdrawals and no-shows at each stage, and which sources bring people who stay.</p>
        </div>
        {data && <ExportLinks report={REPORT} filters={data.filters} />}
      </header>

      {data && <AnalyticsFilters filters={data.filters} audience={data.audience} onChange={setFilters} hasSource />}

      {!data ? (
        <Card>
          <SkeletonLines count={8} />
        </Card>
      ) : (
        <Body data={data} />
      )}
    </div>
  );
}

function Body({ data }: { readonly data: DropOff }): ReactNode {
  const area = useToneArea();
  const filters = data.filters;
  const reasons = (rows: readonly ReasonCount[]): ReactNode =>
    rows.length === 0 ? (
      <p className={styles.empty}>None.</p>
    ) : (
      <ul className={styles.reasonList}>
        {rows.map((row) => (
          <li key={row.code}>
            <span className={row.isAccountability ? styles.accountable : undefined}>{row.label}</span>
            <span>
              <DeepLink to={analyticsDrillHref(filters, REPORT, row.drill)} className={styles.count}>
                {formatNumber(row.count)}
              </DeepLink>
              <span className={styles.cellMeta}>{row.share === null ? '' : `${percent(row.share)} of the stage`}</span>
            </span>
          </li>
        ))}
      </ul>
    );
  return (
    <>
      <DictionaryLine filters={filters} version={data.dictionaryVersion} publishedOn={data.dictionaryPublishedOn} />

      <div
        id="drop-off-accountability"
        className={`${styles.alert} ${toneCardClass()}`}
        data-tone={area.tone}
        role="region"
        aria-label="Process and experience withdrawals"
      >
        <h2 className={styles.alertTitle}>
          <ToneIcon tone={area.tone} icon={area.icon} />
          {data.accountability.total === 0 ? 'No one withdrew over the process or their experience' : `${formatNumber(data.accountability.total)} withdrew over the process or their experience`}
        </h2>
        <p className={styles.meta}>
          “Process took too long” and “Poor experience during the process” are candidates telling you the operation is failing (§L20). <HowCounted metric="process_withdrawals" />
        </p>
        {data.accountability.total > 0 && reasons(data.accountability.byReason)}
      </div>

      <div className={styles.stages}>
        {data.stages.map((stage) => (
          <Card
            key={stage.stage}
            title={COMMAND_FUNNEL_LABELS[stage.stage]}
            meta={`${formatNumber(stage.entered)} entered · ${formatNumber(stage.totalLost)} left${stage.lostShare === null ? '' : ` (${percent(stage.lostShare)})`}`}
            action={<HowCounted metric="drop_off" />}
          >
            <h3 className={styles.subhead}>Rejected</h3>
            {reasons(stage.rejections)}
            <h3 className={styles.subhead}>Withdrew</h3>
            {reasons(stage.withdrawals)}
            <h3 className={styles.subhead}>No-shows</h3>
            <p className={styles.empty}>
              <DrillCount figure={stage.noShows} report={REPORT} filters={filters} />
            </p>
          </Card>
        ))}
      </div>

      <SourceTable data={data} />
    </>
  );
}

type SourceLine = DropOff['sources'][number];
type SourceCount = 'applications' | 'qualified' | 'interviewed' | 'hired';

const SOURCE_SORT: SortReaders<SourceLine> = {
  source: (row) => row.label,
  applications: (row) => row.applications,
  qualified: (row) => row.qualified,
  interviewed: (row) => row.interviewed,
  hired: (row) => row.hired,
  hireRate: (row) => row.hireRate ?? -1,
};

/** Source effectiveness as a table, not a card (DECISIONS-LOG 2026-10-06). Each count still opens its records. */
function SourceTable({ data }: { readonly data: DropOff }): ReactNode {
  const filters = data.filters;
  const [sort, setSort] = useState<readonly SortSpec[]>([]);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const countColumn = (id: SourceCount, header: string): Column<SourceLine> => ({
    id,
    header,
    isNumeric: true,
    isSortable: true,
    defaultWidth: 130,
    cell: (row) => <DrillCount figure={{ value: row[id], drill: `${row.drill}:${id}` }} report={SOURCES} filters={filters} />,
    exportValue: (row) => row[id],
  });
  const columns: readonly Column<SourceLine>[] = [
    { id: 'source', header: 'Source', isIdentity: true, isSortable: true, defaultWidth: 200, cell: (row) => row.label },
    countColumn('applications', 'Applications'),
    countColumn('qualified', 'Qualified'),
    countColumn('interviewed', 'Interviewed'),
    countColumn('hired', 'Hired'),
    { id: 'hireRate', header: 'Hire %', isNumeric: true, isSortable: true, defaultWidth: 100, cell: (row) => (row.hireRate === null ? null : percent(row.hireRate)) },
    // Money is never sorted: each source can hold several currencies, and they are never added together (Q17).
    { id: 'spend', header: 'Spend', isNumeric: true, defaultWidth: 180, cell: (row) => (row.spend.length === 0 ? null : moneyList(row.spend)) },
    { id: 'costPerHire', header: 'Cost per hire', isNumeric: true, defaultWidth: 180, cell: (row) => (row.costPerHire.length === 0 ? null : moneyList(row.costPerHire)) },
  ];
  return (
    <TableSection id="drop-off-sources" title="Source effectiveness" meta="Applications made in the period, how far they have got, and what each source cost" action={<HowCounted metric="source_effectiveness" />}>
      {data.sources.length === 0 ? (
        <p className={styles.empty}>No applications in this period.</p>
      ) : (
        <DataTable
          screenId="report-drop-off-sources"
          label="Source effectiveness"
          columns={columns}
          rows={sortRows(data.sources, sort, SOURCE_SORT)}
          getRowId={(row) => row.source}
          total={data.sources.length}
          sort={sort}
          onSortChange={setSort}
          selection={selection}
          onSelectionChange={setSelection}
          isSelectable={false}
          exportName="source-effectiveness"
        />
      )}
    </TableSection>
  );
}
